/**
 * Générateur — Résoudre des équations du type ax = c ou x + b = c par des
 * méthodes arithmétiques s'appuyant sur les opérations inverses.
 * Attendu ID : 943 | Niveau : 5ème | Domaine : Calcul littéral (cluster 9, O45)
 *
 * ⚠️ MAPPING : O45 → 943 REMAPPÉ À LA MAIN (session 1) — le libellé du doc est
 * identique mot pour mot à l'attendu 943 en base ; le score fuzzy de 0.57
 * venait d'un artefact de parsing (texte d'exercice collé au libellé).
 *
 * Source : doc prof, cluster 9 (Ex.4E balances p74, Ex.5E ovales p75,
 * Ex.6E premières équations, Ex.7E Mathieu — dépiautés du bloc « Créé n°29 »)
 * + Sésamath S6 « Résoudre une équation » en appoint (ex.3 barres, ex.9-14).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « balances » — Ex.4E + S6 ex.3 : SVG reconstruits (balance en
 *   équilibre : k tubes ⟷ masse ; schéma en barres : k parts égales +
 *   accolade totale, l'habillage cartes cadeau du doc). Données AUSSI dans
 *   le texte (accessibilité + solveur). texte (saisie, suffixe g/€) +
 *   texte_trou_saisie (étapes kx = m → x = m ÷ k). Solutions entières par
 *   construction (m = k·x).
 *
 *   Carte « inverses » — Ex.5E (les ovales) : texte_trou_drag (« on applique
 *   aux deux membres : {{soustraire 5}}\nx = {{-7}} » — étiquettes
 *   opérations + valeurs) + texte_trou_saisie FRACTION (3x = 7 → x = 7/3,
 *   fraction IRRÉDUCTIBLE par construction, consigne « écris en fraction
 *   (ex. : 5/4 — dénominateur HORS pool, l'exemple ne peut jamais être la
 *   réponse) » — pas de virgule, saisie sûre ; l'automatisme E4 du doc).
 *
 *   Carte « resoudre » — Ex.6E + S6 ex.9-14 : texte_trou_saisie « x = {{c}} ÷
 *   {{a}}\nx = {{val}} » (les 4 opérations inverses en rotation, résultats
 *   négatifs signés ASCII). Les cas à second membre nul du cadrage (2x = 0,
 *   x ÷ 2 = 0, t + 2,5 = 2,5) sont EXCLUS du tirage depuis l'arbitrage D7 du
 *   bilan prof (les trous valaient 0 sans mobiliser l'opération inverse —
 *   l'exclusion emporte aussi t + 2,5 = 2,5, non visé par le grief) ; leur
 *   code est conservé, réactivable en remettant 'zero' dans
 *   FAMILLES_RESOLUTION.
 *   + qcm_unique (distracteurs : c + b au lieu de c − b, b − c, opération
 *   directe au lieu d'inverse).
 *
 *   Carte « exacte » — Ex.7E Mathieu (l'exercice pivot du doc) : qcm_unique
 *   (« 1,67 est-il solution de 3x = 5 ? » — produit a × arrondi recalculé en
 *   CENTIÈMES exacts, ≠ c par construction car c/a n'a pas d'écriture
 *   décimale : a ∈ {3, 6, 7, 9}, c premier avec les facteurs 3/7 de a) +
 *   tableau_vf (solution exacte / arrondi / encadrement).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 943;

const L_BALANCES = 'Balances et schémas en barres';
const L_INVERSES = 'La même opération des deux côtés';
const L_RESOUDRE = 'Résoudre avec l\'opération inverse';
const L_EXACTE = 'Valeur exacte ou valeur approchée ?';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement — en SAISIE : ASCII « - »)

const CONSIGNE_SIGNE = ' Si la valeur est négative, écris-la avec son signe (ex. : -7).';

// pgcd euclidien (fractions irréductibles par construction)
const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

// ═════════ SVG — balance en équilibre ═════════

const svgBalance = (nbTubes, gauche, droites) => {
    // fléau + plateaux ; à gauche nbTubes tubes (rects arrondis) + éventuelle
    // masse `gauche`, à droite les boîtes `droites` (étiquettes)
    const W = 320, H = 150;
    const cx = W / 2, beamY = 38;
    const p = [];
    // socle + colonne + fléau
    p.push(`<polygon points='${cx - 22},${H - 8} ${cx + 22},${H - 8} ${cx},${beamY}' fill='#C7D2FE' stroke='#1E3A8A' stroke-width='2'/>`);
    p.push(`<line x1='${cx - 105}' y1='${beamY}' x2='${cx + 105}' y2='${beamY}' stroke='#1E3A8A' stroke-width='3'/>`);
    // plateaux (suspentes + lignes)
    [[cx - 105, 'g'], [cx + 105, 'd']].forEach(([px]) => {
        p.push(`<line x1='${px}' y1='${beamY}' x2='${px - 34}' y2='${beamY + 40}' stroke='#1E3A8A' stroke-width='1.5'/>`);
        p.push(`<line x1='${px}' y1='${beamY}' x2='${px + 34}' y2='${beamY + 40}' stroke='#1E3A8A' stroke-width='1.5'/>`);
        p.push(`<line x1='${px - 40}' y1='${beamY + 40}' x2='${px + 40}' y2='${beamY + 40}' stroke='#1E3A8A' stroke-width='3'/>`);
    });
    // tubes à gauche (posés sur le plateau)
    const baseG = cx - 105, plateauY = beamY + 40;
    const totalG = nbTubes * 16 + (gauche ? 40 : 0);
    let gx = baseG - totalG / 2;
    for (let i = 0; i < nbTubes; i++) {
        p.push(`<rect x='${gx}' y='${plateauY - 30}' width='12' height='30' rx='4' fill='#4ADE80' stroke='#1E3A8A' stroke-width='1.5'/>`);
        gx += 16;
    }
    if (gauche) {
        p.push(`<rect x='${gx}' y='${plateauY - 24}' width='38' height='24' fill='#fff' stroke='#1E3A8A' stroke-width='1.5'/>`);
        p.push(`<text x='${gx + 19}' y='${plateauY - 8}' text-anchor='middle' font-size='11' fill='#1E3A8A' font-family='sans-serif'>${gauche}</text>`);
    }
    // boîtes à droite
    const baseD = cx + 105;
    let dx = baseD - (droites.length * 44) / 2;
    droites.forEach((lbl) => {
        p.push(`<rect x='${dx}' y='${plateauY - 24}' width='40' height='24' fill='#fff' stroke='#1E3A8A' stroke-width='1.5'/>`);
        p.push(`<text x='${dx + 20}' y='${plateauY - 8}' text-anchor='middle' font-size='11' fill='#1E3A8A' font-family='sans-serif'>${lbl}</text>`);
        dx += 44;
    });
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}'>${p.join('')}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

// ═════════ SVG — schéma en barres (k parts égales, total accolé) ═════════

const svgBarres = (k, etiquettePart, total) => {
    const w = Math.min(64, Math.round(280 / k));
    const x0 = 20, y0 = 22, h = 34;
    const p = [];
    for (let i = 0; i < k; i++) {
        p.push(`<rect x='${x0 + i * w}' y='${y0}' width='${w}' height='${h}' fill='${i % 2 ? '#C7D2FE' : '#E0E7FF'}' stroke='#1E3A8A' stroke-width='2'/>`);
        p.push(`<text x='${x0 + i * w + w / 2}' y='${y0 + h / 2 + 5}' text-anchor='middle' font-size='13' fill='#1E3A8A' font-family='sans-serif'>${etiquettePart}</text>`);
    }
    const xFin = x0 + k * w;
    // accolade simple (trait + pattes) sous la barre, étiquetée par le total
    p.push(`<line x1='${x0}' y1='${y0 + h + 10}' x2='${xFin}' y2='${y0 + h + 10}' stroke='#1E3A8A' stroke-width='1.5'/>`);
    p.push(`<line x1='${x0}' y1='${y0 + h + 4}' x2='${x0}' y2='${y0 + h + 16}' stroke='#1E3A8A' stroke-width='1.5'/>`);
    p.push(`<line x1='${xFin}' y1='${y0 + h + 4}' x2='${xFin}' y2='${y0 + h + 16}' stroke='#1E3A8A' stroke-width='1.5'/>`);
    p.push(`<text x='${(x0 + xFin) / 2}' y='${y0 + h + 30}' text-anchor='middle' font-size='13' fill='#1E3A8A' font-family='sans-serif'>${total}</text>`);
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${xFin + 20} ${y0 + h + 40}'>${p.join('')}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

// ═════════ Carte 1 — Balances et schémas en barres (Ex.4E + S6 ex.3) ═════════

/** Un contexte balance/barres { texte, elements, k, total, x, unite }. */
const tirerBalance = () => {
    const variante = randomChoice(['tubes', 'tubesPlusMasse', 'barres']);
    if (variante === 'tubes') {
        const k = randInt(2, 5);
        const x = randInt(5, 40);
        const m = k * x;
        return {
            variante, k, x, unite: 'g', total: m,
            texte: `<p><b>La balance est en équilibre : ${k} tubes identiques pèsent autant que ${m} g.</b></p>`,
            elements: svgBalance(k, null, [`${m} g`]),
        };
    }
    if (variante === 'tubesPlusMasse') {
        // 1 tube + a g ⟷ b g + c g — x > 0 PAR CONSTRUCTION : on tire x et a,
        // puis on découpe x + a en b + c (dizaines entières)
        const x = randInt(1, 8) * 10;
        const a = randInt(2, 8) * 10;
        const somme = x + a;
        const c = randInt(1, somme / 10 - 1) * 10;
        const b = somme - c;
        return {
            variante, a, b, c, x, unite: 'g', total: b + c,
            texte: `<p><b>La balance est en équilibre : 1 tube et une masse de ${a} g pèsent autant que ${b} g et ${c} g.</b></p>`,
            elements: svgBalance(1, `${a} g`, [`${b} g`, `${c} g`]),
        };
    }
    const k = randomChoice([3, 4, 5, 6]);
    const x = randomChoice([5, 8, 10, 12, 15, 20]);
    const T = k * x;
    return {
        variante, k, x, unite: '€', total: T,
        texte: `<p><b>Pour un cadeau commun, ${k} amis donnent chacun la même somme. `
            + `Le total récolté est de ${T} € (schéma en barres ci-dessous).</b></p>`,
        elements: svgBarres(k, 'x €', `${T} €`),
    };
};

const genBalancesSaisie = () => {
    const t = tirerBalance();
    const question = t.variante === 'barres'
        ? 'Combien chaque ami donne-t-il, en € ?'
        : 'Quelle est la masse d\'un tube, en g ?';
    const payload = makeSaisie(String(t.x),
        t.texte + `<p>${question}</p>`,
        { ...opts(L_BALANCES), suffixe: t.unite, elements_additionnels: t.elements });
    payload._meta.params = { variante: t.variante, x: t.x };
    return payload;
};

const genBalancesTrous = () => {
    const variante = randomChoice(['tubes', 'barres']);
    const t = (() => {
        for (let essai = 0; essai < 40; essai++) {
            const cand = tirerBalance();
            if (cand.variante === variante) return cand;
        }
        // Repli déterministe : contexte 'tubes' garanti (k défini) — la phrase
        // multiplicative ci-dessous exige k, jamais le variant additif tubesPlusMasse.
        const k = randInt(2, 5), x = randInt(5, 40), m = k * x;
        return {
            variante: 'tubes', k, x, unite: 'g', total: m,
            texte: `<p><b>La balance est en équilibre : ${k} tubes identiques pèsent autant que ${m} g.</b></p>`,
            elements: svgBalance(k, null, [`${m} g`]),
        };
    })();
    // Diviseur k laissé EN DUR (déjà visible dans « k × x = total ») : le recopier
    // n'exige aucun calcul ; seul le résultat de la division est à trouver.
    const phrase = `${t.k} × x = ${t.total}, donc x = ${t.total} ÷ ${t.k} = {{${t.x}}} ${t.unite}`;
    const payload = makeTexteTrou(phrase,
        t.texte + '<p>Complète la résolution.</p>',
        { ...opts(L_BALANCES), mode: 'saisie', elements_additionnels: t.elements });
    payload._meta.params = { variante: t.variante, k: t.k, x: t.x };
    return payload;
};

// ═════════ Carte 2 — La même opération des deux côtés (Ex.5E) ═════════

/** Une équation { eq, opLabel, opsFausses, x } — solution par construction. */
const tirerInverse = () => {
    const famille = randomChoice(['plus', 'moinsB', 'fois', 'divise']);
    if (famille === 'plus') {
        // x + b = c, c pouvant être négatif (le x + 5 = −2 du doc)
        const b = randInt(2, 9);
        const c = randInt(-9, 9);
        const x = c - b;
        const cAff = c < 0 ? `${MOINS}${-c}` : String(c);
        return {
            famille, x,
            eq: `x + ${b} = ${cAff}`,
            opLabel: `soustraire ${b}`,
            opsFausses: [`ajouter ${b}`, `diviser par ${b}`, `multiplier par ${b}`],
        };
    }
    if (famille === 'moinsB') {
        const b = randInt(2, 9);
        const c = randInt(-9, 9);
        const x = c + b;
        const cAff = c < 0 ? `${MOINS}${-c}` : String(c);
        return {
            famille, x,
            eq: `x ${MOINS} ${b} = ${cAff}`,
            opLabel: `ajouter ${b}`,
            opsFausses: [`soustraire ${b}`, `diviser par ${b}`, `multiplier par ${b}`],
        };
    }
    if (famille === 'fois') {
        const a = randInt(2, 9);
        const x = randInt(2, 12);
        return {
            famille, x,
            eq: `${a}x = ${a * x}`,
            opLabel: `diviser par ${a}`,
            opsFausses: [`multiplier par ${a}`, `soustraire ${a}`, `ajouter ${a}`],
        };
    }
    const a = randomChoice([2, 3, 4, 5]);
    const c = randInt(2, 12);
    return {
        famille, x: a * c,
        eq: `x ÷ ${a} = ${c}`,
        opLabel: `multiplier par ${a}`,
        opsFausses: [`diviser par ${a}`, `ajouter ${a}`, `soustraire ${a}`],
    };
};

const genInversesDrag = () => {
    const t = tirerInverse();
    const xAff = String(t.x);   // ASCII (étiquette cohérente avec la saisie)
    const texte = `<p><b>Pour résoudre ${t.eq}, on applique la MÊME opération aux deux membres.</b> Complète.</p>`;
    const phrase = `Opération appliquée aux deux membres : {{${t.opLabel}}}. On obtient x = {{${xAff}}}.`;
    const distracteurs = filtrerDistracteurs(phrase, shuffle([
        ...t.opsFausses,
        String(-t.x),                     // le signe oublié/inversé
        String(t.x + 1),
    ])).slice(0, 4);
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_INVERSES), mode: 'drag', distracteurs });
    payload._meta.params = { famille: t.famille, x: t.x };
    return payload;
};

const genInversesFractionTrous = () => {
    // ax = c avec c/a irréductible ET sans écriture décimale par construction
    // (a contient un facteur 3 ou 7 — sinon « 0,75 » serait une écriture
    // exacte concurrente de la fraction demandée)
    let a = randomChoice([3, 6, 7, 9]);
    let c = randInt(2, 12);
    for (let essai = 0; essai < 30 && (c % a === 0 || pgcd(c, a) !== 1); essai++) c = randInt(2, 12);
    if (c % a === 0 || pgcd(c, a) !== 1) { a = 3; c = 7; }   // repli déterministe
    // Fraction à deux cases avec étais (gabarit 622) à la place du trou
    // « c/a » tapé avec une barre (audit fractions en texte, 2026-09-06) :
    // l'élève remplit numérateur et dénominateur d'une vraie fraction. Plus
    // d'exemple de format à recopier dans la consigne.
    const texte = `<p><b>Résous l'équation ${a}x = ${c}.</b> `
        + 'Écris la valeur EXACTE de x sous forme de fraction.</p>';
    const phrase = `$\\text{On divise les deux membres par } {{${a}}} \\text{ : } x = \\dfrac{{{${c}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${a}}}}$`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_INVERSES), mode: 'saisie' });
    payload._meta.params = { a, c };
    return payload;
};

// ═════════ Carte 3 — Résoudre avec l'opération inverse (Ex.6E + S6) ═════════

/**
 * { eq, etapes (phrase à trous), x, negatif } par famille.
 *
 * La famille « zero » (2x = 0 ; x ÷ 2 = 0 ; t + 2,5 = 2,5) n'est PLUS tirée :
 * les deux trous valent 0, l'élève écrit « 0 » deux fois sans jamais mobiliser
 * l'opération inverse, qui est l'objet de la carte. Le code des trois cas est
 * conservé ci-dessous : rétablir le tirage = remettre 'zero' dans la liste
 * ci-dessous (une ligne).
 */
const FAMILLES_RESOLUTION = ['ax', 'plusB', 'moinsB', 'diviseA'];

const tirerResolution = () => {
    const famille = randomChoice(FAMILLES_RESOLUTION);
    if (famille === 'ax') {
        const a = randInt(2, 9);
        const x = randInt(2, 12);
        const c = a * x;
        return {
            famille, x, eq: `${a}x = ${c}`,
            etapes: `x = {{${c}}} ÷ {{${a}}}\nx = {{${x}}}`,
        };
    }
    if (famille === 'plusB') {
        const b = randInt(2, 15);
        const c = randInt(-9, 20);
        const x = c - b;
        const cAff = c < 0 ? `${MOINS}${-c}` : String(c);
        return {
            famille, x, eq: `x + ${b} = ${cAff}`,
            etapes: `x = {{${c}}} ${MOINS} {{${b}}}\nx = {{${x}}}`,
        };
    }
    if (famille === 'moinsB') {
        const b = randInt(2, 15);
        const c = randInt(-9, 15);
        const x = c + b;
        const cAff = c < 0 ? `${MOINS}${-c}` : String(c);
        return {
            famille, x, eq: `x ${MOINS} ${b} = ${cAff}`,
            etapes: `x = {{${c}}} + {{${b}}}\nx = {{${x}}}`,
        };
    }
    if (famille === 'diviseA') {
        const a = randomChoice([2, 3, 4, 5, 7]);
        const c = randInt(2, 12);
        return {
            famille, x: a * c, eq: `x ÷ ${a} = ${c}`,
            etapes: `x = {{${c}}} × {{${a}}}\nx = {{${a * c}}}`,
        };
    }
    // les cas explicités par le cadrage : 2x = 0, x ÷ 2 = 0, t + 2,5 = 2,5
    const cas = randomChoice(['produit', 'quotient', 'somme']);
    if (cas === 'produit') {
        const a = randInt(2, 9);
        return { famille, x: 0, eq: `${a}x = 0`, etapes: `x = {{0}} ÷ ${a}\nx = {{0}}` };
    }
    if (cas === 'quotient') {
        const a = randInt(2, 9);
        return { famille, x: 0, eq: `x ÷ ${a} = 0`, etapes: `x = {{0}} × ${a}\nx = {{0}}` };
    }
    const d = `${randInt(1, 9)},5`;
    return { famille, x: 0, eq: `t + ${d} = ${d}`, etapes: `t = ${d} ${MOINS} ${d}\nt = {{0}}` };
};

const genResoudreTrous = () => {
    const t = tirerResolution();
    const negatif = t.x < 0 || t.etapes.includes(`{{-`);
    const payload = makeTexteTrou(t.etapes,
        `<p><b>Résous l'équation ${t.eq}</b> en écrivant l'opération inverse.`
        + (negatif ? CONSIGNE_SIGNE : '') + '</p>',
        { ...opts(L_RESOUDRE), mode: 'saisie' });
    payload._meta.params = { famille: t.famille, eq: t.eq, x: t.x };
    return payload;
};

const genResoudreQcm = () => {
    const famille = randomChoice(['ax', 'plusB', 'moinsB']);
    let texte, correct, candidats, params;
    if (famille === 'ax') {
        const a = randInt(2, 9);
        const x = randInt(2, 12);
        const c = a * x;
        texte = `<p><b>Résous : ${a}x = ${c}.</b></p>`;
        correct = String(x);
        candidats = [String(c - a), String(c * a), String(c + a)];
        params = { famille, a, c };
    } else if (famille === 'plusB') {
        const b = randInt(2, 15);
        const c = b + randInt(1, 20);
        texte = `<p><b>Résous : x + ${b} = ${c}.</b></p>`;
        correct = String(c - b);
        candidats = [String(c + b), String(b - c), String(c)];
        params = { famille, b, c };
    } else {
        const b = randInt(2, 15);
        const c = randInt(2, 20);
        texte = `<p><b>Résous : x ${MOINS} ${b} = ${c}.</b></p>`;
        correct = String(c + b);
        candidats = [String(c - b), String(b - c), String(c)];
        params = { famille, b, c };
    }
    const distincts = [...new Set(candidats)].filter(v => v !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_RESOUDRE));
    payload._meta.params = params;
    return payload;
};

// ═════════ Carte 4 — Valeur exacte ou approchée ? (Ex.7E Mathieu) ═════════

const PRENOMS_EX = ['Mathieu', 'Léna', 'Rayan', 'Inès'];

/** (a, c) tels que c/a n'a PAS d'écriture décimale, fraction irréductible. */
const tirerExacte = () => {
    const a = randomChoice([3, 6, 7, 9]);
    const POOLS = { 3: [2, 4, 5, 7, 8, 10, 11], 6: [5, 7, 11], 7: [2, 3, 4, 5, 6], 9: [2, 4, 5, 7, 8] };
    const c = randomChoice(POOLS[a]);
    // arrondi au centième de c/a (centièmes entiers)
    const approx100 = Math.round((c * 100) / a);
    const approx = `${Math.floor(approx100 / 100)},${String(approx100 % 100).padStart(2, '0')}`;
    // produit a × approx en centièmes exacts (≠ 100c par construction : c/a non décimale)
    const prod100 = a * approx100;
    const prod = prod100 % 100 === 0
        ? String(prod100 / 100)
        : `${Math.floor(prod100 / 100)},${String(prod100 % 100).padStart(2, '0')}`.replace(/0$/, '');
    return { a, c, approx, prod };
};

const genExacteQcm = () => {
    const prenom = randomChoice(PRENOMS_EX);
    const { a, c, approx, prod } = tirerExacte();
    const texte = `<p><b>${prenom} a trouvé ${approx} comme solution de l'équation ${a}x = ${c}. `
        + `Est-ce exact ?</b> Vérifie en calculant ${a} × ${approx}.</p>`;
    // Fractions en KaTeX de style texte dans ces phrases-choix (audit
    // fractions en texte, 2026-09-06).
    const correct = `Non : ${a} × ${approx} = ${prod} et non ${c} ; la solution exacte est $\\frac{${c}}{${a}}$.`;
    const candidats = [
        `Oui : ${c} ÷ ${a} ≈ ${approx}, donc c'est la solution.`,
        'Oui : un arrondi au centième est toujours une solution exacte.',
        `Non : la solution est $\\frac{${a}}{${c}}$.`,
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte, opts(L_EXACTE));
    payload._meta.params = { prenom, a, c, approx };
    return payload;
};

const genExacteVf = () => {
    const { a, c, approx } = tirerExacte();
    const fl = Math.floor(c / a);
    const lignes = shuffle([
        { l: `$\\frac{${c}}{${a}}$ est la solution exacte de ${a}x = ${c}`, v: true },
        { l: `${approx} est la solution exacte de ${a}x = ${c}`, v: false },
        { l: `L'équation ${a}x = ${c} n'a pas de solution`, v: false },
        { l: `La solution de ${a}x = ${c} est comprise entre ${fl} et ${fl + 1}`, v: true },
    ]);
    const payload = makeVraiFaux(
        lignes.map(x => x.l),
        lignes.map(x => (x.v ? 0 : 1)),
        '<p><b>Vrai ou faux ?</b> Pense à la valeur exacte de la solution.</p>',
        opts(L_EXACTE));
    payload._meta.params = { a, c, approx };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    balances_texte: genBalancesSaisie,
    balances_texte_trou_saisie: genBalancesTrous,
    inverses_texte_trou_drag: genInversesDrag,
    inverses_texte_trou_saisie: genInversesFractionTrous,
    resoudre_texte_trou_saisie: genResoudreTrous,
    resoudre_qcm_unique: genResoudreQcm,
    exacte_qcm_unique: genExacteQcm,
    exacte_tableau_vf: genExacteVf,
};

const FORMAT_LABELS = {
    texte: 'Saisie', texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'balances', label: L_BALANCES, formatsList: buildFormats('balances') },
    { id: 'inverses', label: L_INVERSES, formatsList: buildFormats('inverses') },
    { id: 'resoudre', label: L_RESOUDRE, formatsList: buildFormats('resoudre') },
    { id: 'exacte', label: L_EXACTE, formatsList: buildFormats('exacte') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
